import classnames from 'classnames';
import { useLayoutEffect, useRef, type CSSProperties } from 'react';
import styles from './TimerRing.module.scss';

interface TimerRingProps {
    /** `Date.now()` milliseconds. */
    startedAtMs: number;
    /** `Date.now()` milliseconds. */
    endsAtMs: number;
    /** Ring color. */
    color: string;
    /** `fill`: the ring grows clockwise until done (a crystal coalescing). `drain`: it shrinks away (a cooldown). */
    mode: 'fill' | 'drain';
    /** Where the seconds left sit: a slot's corner, or large in the middle of the ring. */
    secondsPlacement: 'corner' | 'center';
    className?: string;
}

/** The ring's fill for the share of the time gone: it grows (`fill`) or empties (`drain`). */
function toProgress(mode: TimerRingProps['mode'], elapsed: number): number {
    return mode === 'fill' ? elapsed : 1 - elapsed;
}

function resolveProgress(startedAtMs: number, endsAtMs: number): { elapsed: number; secondsLeft: number } {
    const now = Date.now();
    const duration = Math.max(1, endsAtMs - startedAtMs);
    return {
        elapsed: Math.min(1, Math.max(0, (now - startedAtMs) / duration)),
        secondsLeft: Math.max(0, Math.ceil((endsAtMs - now) / 1000)),
    };
}

/**
 * A ring around a slot that times something, with the seconds left. Animates
 * itself each frame through a CSS variable and the DOM, without re-rendering
 * React, and stops once the time is up.
 */
export function TimerRing({ startedAtMs, endsAtMs, color, mode, secondsPlacement, className }: TimerRingProps): JSX.Element {
    const ringRef = useRef<HTMLSpanElement>(null);
    const secondsRef = useRef<HTMLSpanElement>(null);
    const initial = resolveProgress(startedAtMs, endsAtMs);

    // The seconds are written here only, never by React: React would keep updating a text node this replaced.
    // Before paint, so the first frame already shows them.
    useLayoutEffect(() => {
        let frame = 0;
        const tick = (): void => {
            const { elapsed, secondsLeft } = resolveProgress(startedAtMs, endsAtMs);
            ringRef.current?.style.setProperty('--progress', String(toProgress(mode, elapsed)));
            if (secondsRef.current) {
                secondsRef.current.textContent = `${secondsLeft}s`;
            }
            if (elapsed < 1) {
                frame = requestAnimationFrame(tick);
            }
        };
        tick();
        return (): void => cancelAnimationFrame(frame);
    }, [startedAtMs, endsAtMs, mode]);

    const style = { '--progress': toProgress(mode, initial.elapsed), '--ring-color': color } as CSSProperties;
    return (
        <span className={classnames(styles.timerRing, className)} style={style} aria-hidden='true'>
            <span ref={ringRef} className={styles.ring} />
            <span ref={secondsRef} className={classnames(styles.seconds, secondsPlacement === 'center' && styles.center)} />
        </span>
    );
}
